<template>
  <Modal
    class="project-analysis-text-modal modal-themes-edit"
    header-tag="h3"
    :isEditThemes="true"
    :ref="modalRef"
    :show="show"
    :reset-listener="resetListener"
    @close="closeModal"
  >
    <template #header>
      <div style="display: flex; flex-direction: row; justify-content: space-between; width: 100%">
        <h3 style="display: flex">
          <VisibleText>Edit themes</VisibleText>
        </h3>
        <div style="display: flex">
          <button
            type="button"
            class="theme__helper-options-btn"
            @click="$emit('bannedKeywordsModalIsVisible')"
            style="margin-right: 1rem; font-size: 1rem"
          >
            <SvgIconDecorative icon="ban" />
            <VisibleText style="font-size: 1rem">Show banned keywords</VisibleText>
          </button>
        </div>
      </div>
    </template>

    <div style="padding: 1em; margin-top: -2em">
      <div v-if="!themeList.length">
        <VisibleText>No themes found.</VisibleText>
      </div>
    </div>
    <ThemeList
      :isThemeEdit="true"
      :ref="themeListRef"
      type="keyword"
      :textAnalysisObject="textAnalysisObject"
      :responses="unsortedResponses"
      :themes="themeList"
      :should-focus-first-theme="shouldFocusFirstTheme"
      :key="updateKey"
      :duplicateIndex="duplicateIndex"
      :propReadOnly="propReadOnly"
      :sortedThemes="sortedThemes"
      :hasPreProcessedText="hasPreProcessedText"
      :includeSmartCoverage="includeSmartCoverage"
      @removeTheme="$emit('removeTheme', $event)"
      @updatingSpinner="updateSpinner"
      @updateTextObject="updateTextObject($event)"
      @update-theme-title="renameThemeTitle(themeList[$event.index], $event.title)"
      @applyPatches="applyPatches($event)"
    />
    <div v-if="foundChanges.length">
      <VisibleText>Warning. Unsaved changes applied.</VisibleText>
    </div>
    <template #footer>
      <div class="modal-footer__options">
        <button
          class="btn-default"
          role="button"
          @click.stop="closeModal"
          :disabled="!foundChanges.length || awaitingLoading"
        >
          <div v-if="!foundChanges.length">
            <VisibleText>No changes found</VisibleText>
          </div>
          <div v-else-if="awaitingLoading">
            <VisibleText>Wait for coverage to complete</VisibleText>
          </div>
          <div v-else>
            <VisibleText>Close window</VisibleText>
          </div>
        </button>
        <button
          class="btn-create btn-secondary"
          role="button"
          @click.stop="createTheme"
          :disabled="propReadOnly"
        >
          <VisibleText>Create new theme</VisibleText>
        </button>
      </div>
    </template>
  </Modal>
</template>

<script>
// Mixins
import ThemeListMixin from '../Mixins/themeListMixin.js';
import themeColorsMixin from '../../Mixins/themeColorsMixin.js';
import textAnalysisMixin from '../../Mixins/textAnalysisMixin.js';
import SvgIconDecorative from '@/components/UI/Svg/SvgIconDecorative.vue';

export default {
  name: 'ThemesEdit',
  mixins: [ThemeListMixin, textAnalysisMixin, themeColorsMixin],
  components: { SvgIconDecorative },
  props: {
    show: {
      default: () => false,
      type: Boolean,
    },
    textAnalysisObject: {
      type: Object,
      required: false,
      default: () => {},
    },
    sortedThemes: {
      type: Array,
      default: () => [],
    },
    unsortedResponses: {
      type: Array,
      default: () => [],
    },
    hasPreProcessedText: {
      type: Boolean,
      required: false,
      default: () => false,
    },
    includeSmartCoverage: {
      type: String,
      default: () => 'exclude',
    },
    propReadOnly: {
      default: () => false,
      type: Boolean,
    },
  },
  data() {
    return {
      isSaving: false,
      duplicateIndex: -1,
      suggestedThemes: [],
      spinnerQueue: [],
    };
  },
  created() {
    if (this.sortedThemes) {
      if (this.sortedThemes.length) {
        this.themeList = this.deepCloneObj(this.sortedThemes);
      }
    }
  },
  computed: {
    parentThemesLength() {
      let length = 0;
      if (this.sortedThemes) {
        if (this.sortedThemes.length) {
          length = this.sortedThemes.themes;
        }
      }
      return length;
    },
    awaitingLoading() {
      if (this.spinnerQueue.length) {
        return true;
      }
      return false;
    },
    foundChanges() {
      let changedThemes = [];
      if (!this.themeList || !this.themeList.length) return changedThemes;
      let themes = this.deepCloneObj(this.themeList);
      // if (!this.sortedThemes.length && themes.length) return themes

      // -1 means a change has been made but update all themes regardless of index
      // if (!this.sortedThemes.length && themes.length) return [-1]
      // if (this.sortedThemes.length !== this.themesList.length) return [-1]
      if (this.sortedThemes) {
        for (let x = 0; x < themes.length; x++) {
          // Each theme has to find at least one matching UUID
          let foundMatchingUUID = false;
          for (let i = 0; i < this.sortedThemes.length; i++) {
            if (themes[x]._id === this.sortedThemes[i]._id) {
              foundMatchingUUID = true;
              // name changed
              if (themes[x].name !== this.sortedThemes[i].name) {
                if (!changedThemes.includes(i)) {
                  changedThemes.push(i);
                }
              }
              // new coverage
              if (themes[x].coverage.total != this.sortedThemes[i].coverage.total) {
                if (!changedThemes.includes(i)) {
                  changedThemes.push(i);
                }
              }
              // new filtered coverage
              if (
                themes[x].filtered_coverage.total != this.sortedThemes[i].filtered_coverage.total
              ) {
                if (!changedThemes.includes(i)) {
                  changedThemes.push(i);
                }
              }
              // new keyword length

              const currentKeywords = this.normalizeKeywords(themes[x]);
              const previousKeywords = this.normalizeKeywords(this.sortedThemes[i]);
              if (currentKeywords.length !== previousKeywords.length) {
                changedThemes.push(i);
              }

              // new keyword found
              const foundMatch = currentKeywords.map((kw) =>
                previousKeywords.includes(kw) ? 1 : 0,
              );

              if (foundMatch.includes(0)) {
                changedThemes.push(i);
              }
            }
          }
          // entirely new theme, refresh all to be safe, can be optimized later
          if (!foundMatchingUUID) {
            changedThemes.push(x);
          }
        }
      }
      return [...new Set(changedThemes)];
    },
  },
  watch: {
    parentThemesLength: async function (val) {
      if (!val) return;
      if (this.sortedThemes) {
        if (this.sortedThemes.length) {
          this.themeList = this.deepCloneObj(this.sortedThemes);
        }
      }
    },
    show: function (val) {
      if (val == true) {
        if (this.sortedThemes) {
          if (this.sortedThemes.length) {
            this.themeList = this.deepCloneObj(this.sortedThemes);
          }
        }
      } else {
        this.themeList = [];
      }
    },
  },
  methods: {
    refreshThemesFromParent() {
      //let useTheseThemes = []
      this.themeList = this.deepCloneObj(this.sortedThemes);
    },
    updateTextObject(cleanedProject) {
      let object = {
        project: cleanedProject,
        scroll: 'themes',
      };
      this.$emit('updateTextObject', object);
    },
    updateSpinner(val) {
      this.spinnerQueue = val;
    },
    async renameThemeTitle(theme, title) {
      if (this.themeList) {
        let themes = this.deepCloneObj(this.themeList);
        if (typeof title !== 'string' || title.trim() === '') {
          title = 'unnamed theme';
        }
        let foundIndex = -1;
        let foundExisting = false;
        for (let i = 0; i < themes.length; i++) {
          if (themes[i]._id === theme._id) {
            foundIndex = i;
          } else {
            if (themes[i].name && themes[i].name.toLowerCase() == title.toLowerCase()) {
              foundExisting = true;
            }
          }
        }
        if (foundIndex > -1 && !foundExisting) {
          themes[foundIndex].name = title;
          this.themeList = themes;
          this.$emit('updateExistingThemeTitle', themes[foundIndex], title);
        }
        if (foundIndex > -1 && foundExisting) {
          this.duplicateIndex = foundIndex;
          setTimeout(() => {
            this.duplicateIndex = -1;
          }, 6000);
        }
      }
    },
    async applyPatches(patches) {
      for (const patch of patches) {
        await this.$emit('updateTheme', patch);
      }
    },
  },
};
</script>
